import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface UseCustomPanelFormOptions<TServer, TDraft> {
  /** GET URL — also used as PUT URL by default save. */
  endpoint: string;
  /** Build initial draft from server payload. Called every time server data
   *  arrives (initial load + after successful save) to reset draft.
   *  Captured by ref — no stability requirement for consumers. */
  toDraft: (server: TServer) => TDraft;
  /** Compute dirty flag. Default: `JSON.stringify(toDraft(server)) !== JSON.stringify(draft)`.
   *  Default is key-order sensitive — pass custom isDirty for complex shapes
   *  where draft and server may have different key insertion order.
   *  Captured by ref — no stability requirement for consumers. */
  isDirty?: (server: TServer, draft: TDraft) => boolean;
  /** Build PUT body from draft. Default: send draft as-is. */
  toBody?: (draft: TDraft) => unknown;
  /** Override the default save behavior entirely. Must return fresh server payload. */
  save?: (draft: TDraft) => Promise<TServer>;
  /** Notify parent of dirty transitions. Called once on mount with initial value
   *  (typically false), then on each toggle. */
  onDirtyChange?: (dirty: boolean) => void;
}

export interface UseCustomPanelFormResult<TServer, TDraft> {
  server: TServer | undefined;
  draft: TDraft;
  setDraft: React.Dispatch<React.SetStateAction<TDraft>>;
  dirty: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error: Error | null;
  save: () => Promise<void>;
  reset: () => void;
}

export function useCustomPanelForm<TServer, TDraft>({
  endpoint,
  toDraft,
  isDirty,
  toBody,
  save: saveOverride,
  onDirtyChange,
}: UseCustomPanelFormOptions<TServer, TDraft>): UseCustomPanelFormResult<
  TServer,
  TDraft
> {
  const qc = useQueryClient();

  const query = useQuery<TServer, Error>({
    queryKey: ["endpoint", endpoint],
    queryFn: async () => {
      const r = await fetch(endpoint, { credentials: "include" });
      if (!r.ok) throw new Error(`${endpoint}: ${r.status}`);
      return r.json();
    },
  });

  const server = query.data;

  // Stash toDraft and isDirty in refs so inline arrow fns passed by consumers
  // don't cause the draft-reset effect or dirty memo to re-run every render.
  const toDraftRef = React.useRef(toDraft);
  toDraftRef.current = toDraft;
  const isDirtyRef = React.useRef(isDirty);
  isDirtyRef.current = isDirty;

  // Initialize draft from server when it arrives, and reset on subsequent
  // server changes (e.g., after successful save returns fresh payload).
  const [draft, setDraft] = React.useState<TDraft>(() =>
    server ? toDraftRef.current(server) : ({} as TDraft),
  );
  React.useEffect(() => {
    if (server !== undefined) setDraft(toDraftRef.current(server));
  }, [server]);

  const dirty = React.useMemo(() => {
    if (!server) return false;
    const currentIsDirty = isDirtyRef.current;
    if (currentIsDirty) return currentIsDirty(server, draft);
    return JSON.stringify(toDraftRef.current(server)) !== JSON.stringify(draft);
  }, [server, draft]);

  React.useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const mutation = useMutation<TServer, Error, void>({
    mutationFn: async () => {
      if (saveOverride) return saveOverride(draft);
      const body = toBody ? toBody(draft) : draft;
      const r = await fetch(endpoint, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: (fresh) => {
      qc.setQueryData(["endpoint", endpoint], fresh);
    },
  });

  const save = React.useCallback(async () => {
    if (!dirty || mutation.isPending) return;
    await mutation.mutateAsync();
  }, [dirty, mutation]);

  const reset = React.useCallback(() => {
    if (server !== undefined) setDraft(toDraftRef.current(server));
  }, [server]);

  return {
    server,
    draft,
    setDraft,
    dirty,
    isLoading: query.isPending,
    isSaving: mutation.isPending,
    error: (mutation.error ?? query.error) as Error | null,
    save,
    reset,
  };
}
